import 'react-loading-skeleton/dist/skeleton.css'
import 'react-toastify/dist/ReactToastify.css'
import 'theme/Fonts.css'

import { initializeApp } from 'firebase/app'
import { connectAuthEmulator, getAuth } from 'firebase/auth'
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions'
import { getPerformance } from 'firebase/performance'
import { Toast } from 'fitify-ui/src/Toast'
import { errorReporter } from 'fitify-ui/src/utils/error-reporter'
import { GrowthBookProvider } from 'fitify-ui/src/utils/growthbook'
import ErrorBoundary from 'fitify-ui-onboarding/src/components/Error/ErrorBoundary'
import { AuthUserContextProvider } from 'fitify-ui-onboarding/src/contexts/AuthUserContext'
import { EmailUserContextProvider } from 'fitify-ui-onboarding/src/contexts/EmailUserContext'
import { EventContextProvider } from 'fitify-ui-onboarding/src/contexts/EventContext'
import { FormInputContextProvider } from 'fitify-ui-onboarding/src/contexts/FormInputContext'
import { PromoContextProvider } from 'fitify-ui-onboarding/src/contexts/PromoContext'
import {
  type CacheData,
  type CacheValue,
  CacheContext,
} from 'fitify-ui-onboarding/src/utils/cache'
import { defaultFeatures } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { appWithTranslation } from 'next-i18next/pages'
import { useEffect, useMemo, useState } from 'react'

import { App } from 'components/App'
import { AppPageTitle } from 'components/AppPageTitle'
import { useAnalytics } from 'hooks/useAnalytics'

import nextI18NextConfig from '../../next-i18next.config'

declare global {
  interface Window {
    // Used in VWO
    updateOptimizeConfig: (optimizeConfig: Record<string, unknown>) => void
    optimizeConfig: Record<string, unknown>
  }
}

const app = initializeApp({
  apiKey: process.env.NEXT_PUBLIC_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_AUTH_DOMAIN,
  databaseURL: process.env.NEXT_PUBLIC_DATABASE_URL,
  projectId: process.env.NEXT_PUBLIC_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_MEASUREMENT_ID,
})

if (process.env.NEXT_PUBLIC_FUNCTIONS_EMULATOR_PORT) {
  // Connects to functions emulator
  connectFunctionsEmulator(
    getFunctions(),
    'localhost',
    parseInt(process.env.NEXT_PUBLIC_FUNCTIONS_EMULATOR_PORT)
  )
}

// Connects to auth emulator
if (process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_URL) {
  const auth = getAuth()
  connectAuthEmulator(auth, process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_URL)
}

function FitifyApp({ Component, pageProps }: AppProps) {
  const [cacheData, setCacheData] = useState<CacheData>({})
  const firebaseAuth = getAuth()

  const setCacheKey = (key: string, value: CacheValue) => {
    setCacheData((cache) => ({ ...cache, [key]: value }))
  }

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      if (firebaseAuth.currentUser) {
        errorReporter.setUser(firebaseAuth.currentUser.uid)
      }

      const handleError = (event: ErrorEvent) => {
        void errorReporter.report(event.error, event.message)
      }

      const handleRejection = (event: PromiseRejectionEvent) => {
        void errorReporter.report(event.reason)
      }

      window.addEventListener('error', handleError)
      window.addEventListener('unhandledrejection', handleRejection)

      getPerformance(app)

      return () => {
        window.removeEventListener('error', handleError)
        window.removeEventListener('unhandledrejection', handleRejection)
      }
    }
  }, [firebaseAuth.currentUser])

  useAnalytics()

  const cacheContextProviderValue = useMemo(
    () => ({
      data: cacheData,
      set: setCacheKey,
    }),
    [cacheData]
  )

  return (
    <>
      <Head>
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
        />
      </Head>
      <AppPageTitle pageMeta={pageProps.pageMeta} />
      <CacheContext.Provider value={cacheContextProviderValue}>
        <ErrorBoundary isProduction={process.env.NODE_ENV === 'production'}>
          <GrowthBookProvider defaultFeatures={defaultFeatures}>
            <AuthUserContextProvider>
              <EmailUserContextProvider>
                <PromoContextProvider>
                  <EventContextProvider>
                    <FormInputContextProvider>
                      <App Component={Component} pageProps={pageProps} />
                    </FormInputContextProvider>
                  </EventContextProvider>
                </PromoContextProvider>
              </EmailUserContextProvider>
            </AuthUserContextProvider>
            <Toast />
          </GrowthBookProvider>
        </ErrorBoundary>
      </CacheContext.Provider>
    </>
  )
}

export default appWithTranslation(FitifyApp, nextI18NextConfig)
